import { useCallback, useEffect, useState } from "react";
import { translate, type AppLocale } from "../i18n";
import {
  getOrderDocumentDownload, getOrderDocuments, getOrderStatus,
  type CustomerDocument, type OrderPaidStatus
} from "../lib/api";
import { apiReturnUrl } from "../lib/return-url";
import { BRAND } from "../lib/brand";
import { formatMoney } from "../lib/money";
import { trackingCopy } from "./tracking-copy";
import { flowCopy } from "./flow-copy";

type State =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; status: OrderPaidStatus; documents: CustomerDocument[] | null };

/** Token-in-URL customer status: works from an email in a new tab or device. */
export function Tracking({ token, locale }: { token: string; locale: AppLocale }) {
  const copy = trackingCopy[locale];
  const [state, setState] = useState<State>({ kind: "loading" });
  const [retry, setRetry] = useState(0);
  const [download, setDownload] = useState<{ id: string; url: string } | null>(null);
  const [documentError, setDocumentError] = useState(false);
  const [documentBusy, setDocumentBusy] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ kind: "loading" });
    setDownload(null);
    void getOrderStatus(token, controller.signal).then(async (status) => {
      if (controller.signal.aborted) return;
      if (!status.fulfillment) {
        setState({ kind: "error" });
        return;
      }
      const documents = await getOrderDocuments(token).then((result) =>
        result.documents.filter((document) => document.status === "issued")
      ).catch(() => null);
      if (!controller.signal.aborted) setState({ kind: "ready", status, documents });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ kind: "error" });
    });
    return () => controller.abort();
  }, [token, retry]);

  const openDocument = useCallback(async (documentId: string) => {
    setDocumentBusy(documentId);
    setDocumentError(false);
    setDownload(null);
    try {
      const result = await getOrderDocumentDownload(token, documentId);
      const url = apiReturnUrl(result.download_url);
      if (!url) throw new Error("Invalid document URL");
      setDownload({ id: documentId, url });
    } catch {
      setDocumentError(true);
    } finally {
      setDocumentBusy(null);
    }
  }, [token]);

  if (state.kind === "loading") return <main className="co-state"><div className="co-spinner" aria-hidden /><p>{copy.title}</p></main>;
  if (state.kind === "error") return <main className="co-state">
    <h1>{copy.title}</h1><p>{copy.unavailable}</p>
    <button className="co-cta" type="button" onClick={() => setRetry((value) => value + 1)}>{copy.retry}</button>
    <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{copy.support}</a>
  </main>;

  const { status, documents } = state;
  const fulfillment = status.fulfillment!;
  const safeTrackingUrl = apiReturnUrl(fulfillment.trackingUrl);
  return <main className="co-page co-tracking">
    <div className="co-tracking-intro">
      <span className="co-diamond" aria-hidden />
      <h1>{copy.title}</h1>
      <p>{copy.intro}</p>
    </div>
    <section className="co-card" aria-label={copy.title}>
      <div className="co-price-row"><span>{copy.title}</span><strong>#{status.orderReference}</strong></div>
      <p className="co-tracking-payment">{status.paid ? copy.paid : copy.paymentPending}</p>
      {status.paid && status.totalMinor !== null && status.currency && (
        <div className="co-price-row co-price-total">
          <span>{translate(locale, "success.paidTotal")}</span>
          <strong>{formatMoney(status.totalMinor, status.currency, locale)}</strong>
        </div>
      )}
      <div className="co-price-row"><span>{copy.orderStatus}</span><strong>{copy.status[fulfillment.status]}</strong></div>
      {fulfillment.carrier && <div className="co-price-row"><span>{copy.carrier}</span><strong>{fulfillment.carrier}</strong></div>}
      {fulfillment.trackingNumber && <div className="co-price-row"><span>{copy.trackingNumber}</span><strong>{fulfillment.trackingNumber}</strong></div>}
      {safeTrackingUrl && <a className="co-btn-secondary" href={safeTrackingUrl} target="_blank" rel="noopener noreferrer">{copy.trackShipment}</a>}
    </section>
    <section className="co-card" id="receipt" aria-label={copy.receipt}>
      <h2 className="co-card-title">{copy.receipt}</h2>
      {documents === null ? <div className="co-tracking-retry">
        <p>{copy.receiptUnavailable}</p>
        <button className="co-btn-secondary" type="button" onClick={() => setRetry((value) => value + 1)}>{copy.retry}</button>
      </div> : documents.length ? documents.map((document) => <div className="co-tracking-document" key={document.id}>
        <span>{document.document_number}</span>
        <button className="co-btn-secondary" disabled={documentBusy !== null} type="button" onClick={() => void openDocument(document.id)}>{copy.getReceipt}</button>
        {download?.id === document.id && <a href={download.url} target="_blank" rel="noopener noreferrer">{copy.openReceipt}</a>}
      </div>) : <p>{copy.noReceipt}</p>}
      {documentError && <p role="alert">{copy.unavailable}</p>}
    </section>
    <a className="co-btn-secondary" href={`/o/${encodeURIComponent(token)}/privacy?lang=${locale}`}>{flowCopy(locale).privacyLink}</a>
    <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{copy.support}</a>
  </main>;
}
